import { UNIT_ORDER } from "./dispatchConstants";
import { clean } from "./dispatchFormatters";

export function hasRole(unit, role) {
  return Array.isArray(unit?.roles) && unit.roles.includes(role);
}

export function isBike(unit) {
  return unit?.category === "bike";
}

export function isCart(unit) {
  return unit?.category === "cart";
}

export function isSupervisor(unit) {
  return unit?.category === "supervisor" || hasRole(unit, "command");
}

export function isAmbulance(unit) {
  return unit?.category === "ambulance" || hasRole(unit, "hospitalTransport");
}

export function isSupport(unit) {
  return unit?.category === "support" || hasRole(unit, "support");
}

export function isCourtesyUnit(unit) {
  return hasRole(unit, "courtesyTransport");
}

export function isResponseUnit(unit) {
  return isBike(unit) || isCart(unit);
}

export function isInService(unit) {
  const status = String(unit?.status || "").trim().toUpperCase();
  return unit?.active !== false && status !== "OOS";
}

export function isAvailable(unit) {
  return isInService(unit) && String(unit?.status || "").trim().toUpperCase() === "AVAIL";
}

/**
 * Courtesy transport units can remain eligible even if already carrying
 * one or more active courtesy trips.
 */
export function isAvailableForCourtesy(unit) {
  return isInService(unit) && (unit?.status === "AVAIL" || isCourtesyUnit(unit));
}

export function normalizeUnitName(name) {
  return clean(name).toUpperCase();
}

export function unitSortKey(unit) {
  const name = normalizeUnitName(unit?.name);
  const fixed = UNIT_ORDER.indexOf(name);
  if (fixed >= 0) return `000_${String(fixed).padStart(3, "0")}`;
  return `999_${name}`;
}

export function byPreferredUnitOrder(a, b) {
  return unitSortKey(a).localeCompare(unitSortKey(b));
}

/**
 * Recommended courtesy unit = dedicated transport unit only.
 * Do not exclude the unit just because it is already on another courtesy trip.
 */
export function isCourtesyRecommendedUnit(unit) {
  return isCourtesyUnit(unit) && isAvailableForCourtesy(unit);
}

/**
 * Courtesy manual add-ons:
 * - allow dedicated courtesy/transport units
 * - allow any available cart
 * - do NOT include supervisors by default
 * - do NOT include bikes
 */
export function isCourtesyDispatchEligible(unit) {
  if (!isInService(unit)) return false;

  // Dedicated transport unit is allowed, even if already on courtesy trips
  if (isCourtesyUnit(unit)) return true;

  // Manual options allowed for HQ transport requests
  if (!isAvailable(unit)) return false;

  return isCart(unit) || isSupervisor(unit);
}

export function getUnitChipStyle(status) {
  switch (status) {
    case "DISP":
      return { background: "#e5e7eb", borderColor: "#9ca3af", color: "#111827" };
    case "ON_SCENE":
      return { background: "#fff7ed", borderColor: "#f97316", color: "#c2410c" };
    case "TO_HQ":
    case "TRANSPORTING":
      return { background: "#eff6ff", borderColor: "#3b82f6", color: "#1d4ed8" };
    case "ARRIVED_HQ":
    case "ARRIVED":
      return { background: "#f0fdf4", borderColor: "#22c55e", color: "#15803d" };
    case "OOS":
      return { background: "#f1f5f9", borderColor: "#94a3b8", color: "#475569" };
    default:
      return { background: "#ffffff", borderColor: "#cbd5e1", color: "#334155" };
  }
}